'use client'

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { hardenEmailFrameHtml } from '@/lib/rendering/email-frame-hardening'
import { createImagePaintLease } from '@/lib/rendering/email-tiptap-viewer/paint-settled-resource'

const MIN_FRAME_HEIGHT = 10
const FRAME_FONT_SETTLE_TIMEOUT_MS = 1500
const FRAME_IMAGE_SETTLE_TIMEOUT_MS = 4000
const FRAME_IMAGE_SETTLE_MAX = 256

// Re-exported from the shared lib module (extracted so surfaces without
// a 'use client' boundary — the analytics document preview — can import
// the hardening pass without pulling in this client component file.
export { hardenEmailFrameHtml }

async function settleWithin(
  promise: Promise<unknown>,
  timeoutMs: number
): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      promise,
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, timeoutMs)
      }),
    ])
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }
  }
}

function afterAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve())
      return
    }
    setTimeout(resolve, 16)
  })
}

export type PixelPerfectEmailFrameProps = {
  /** The exact persisted/delivered HTML document. */
  html: string
  title: string
  className?: string
  style?: CSSProperties
  onHeightChange?: (height: number) => void
  /**
   * Opt-in content-width report. When present, every measurement pass also
   * reports `max(documentElement, body).scrollWidth` (deduped). Because the
   * frame's viewport is pinned by the host, scrollWidth can never fall below
   * the viewport width — so this is an UP-ONLY overflow signal: a report
   * wider than the frame means fixed-width content is being clipped
   * (`scrolling="no"` hides it) and the host should widen the frame.
   * Hosts that size the frame themselves (canvas edit surface) omit it and
   * see zero behavior change.
   */
  onContentWidthChange?: (width: number) => void
  onReady?: () => void
  /** Install same-origin document behavior; return cleanup for reload/unmount. */
  onDocumentReady?: (
    doc: Document,
    frame: HTMLIFrameElement
    // biome-ignore lint/suspicious/noConfusingVoidType: effect-style contract — void lets handlers omit a cleanup without an explicit `return undefined`
  ) => void | (() => void)
}

/**
 * Exact, isolated email-document renderer.
 *
 * An iframe is required here rather than a shadow root or a reconstructed
 * TipTap DOM: imported email styles commonly contain arbitrary viewport media
 * queries, and those must evaluate against this email's width instead of the
 * application viewport. The frame is same-origin only so the parent can size
 * it to the complete document; scripts, forms, popups and navigation escape
 * remain disabled by the sandbox.
 */
export function PixelPerfectEmailFrame({
  html,
  ...props
}: PixelPerfectEmailFrameProps) {
  return <PixelPerfectEmailFrameDocument html={html} {...props} />
}

function PixelPerfectEmailFrameDocument({
  html,
  title,
  className,
  style,
  onHeightChange,
  onContentWidthChange,
  onReady,
  onDocumentReady,
}: PixelPerfectEmailFrameProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const cleanupLayoutRef = useRef<(() => void) | null>(null)
  const cleanupConsumerRef = useRef<(() => void) | null>(null)
  const cancelImageSettleRef = useRef<(() => void) | null>(null)
  const loadedDocumentRef = useRef<Document | null>(null)
  const onDocumentReadyRef = useRef(onDocumentReady)
  const readyGenerationRef = useRef(0)
  const lastReportedHeightRef = useRef<number | null>(null)
  const lastReportedWidthRef = useRef<number | null>(null)
  const [height, setHeight] = useState(MIN_FRAME_HEIGHT)
  const hardenedHtml = useMemo(() => hardenEmailFrameHtml(html), [html])

  const measure = useCallback(() => {
    const frame = frameRef.current
    const doc = frame?.contentDocument
    if (!frame || !doc?.documentElement) {
      return MIN_FRAME_HEIGHT
    }
    // An iframe's scrollHeight cannot fall below its viewport height. Collapse
    // the measurement viewport first so shorter edits shrink instead of
    // retaining the previous document's height.
    frame.style.height = `${MIN_FRAME_HEIGHT}px`
    const next = Math.max(
      MIN_FRAME_HEIGHT,
      doc.documentElement.scrollHeight,
      doc.body?.scrollHeight ?? 0
    )
    frame.style.height = `${next}px`
    setHeight((current) => (current === next ? current : next))
    if (lastReportedHeightRef.current !== next) {
      lastReportedHeightRef.current = next
      onHeightChange?.(next)
    }
    if (onContentWidthChange) {
      const contentWidth = Math.max(
        doc.documentElement.scrollWidth,
        doc.body?.scrollWidth ?? 0
      )
      if (
        Number.isFinite(contentWidth) &&
        contentWidth > 0 &&
        lastReportedWidthRef.current !== contentWidth
      ) {
        lastReportedWidthRef.current = contentWidth
        onContentWidthChange(contentWidth)
      }
    }
    return next
  }, [onHeightChange, onContentWidthChange])

  const settle = useCallback(
    async (generation: number) => {
      let doc = frameRef.current?.contentDocument
      if (!doc) {
        return
      }
      await settleWithin(
        Promise.resolve(doc.fonts?.ready).catch(() => undefined),
        FRAME_FONT_SETTLE_TIMEOUT_MS
      )
      if (
        readyGenerationRef.current !== generation ||
        frameRef.current?.contentDocument !== doc
      ) {
        return
      }
      const leases = Array.from(doc.body?.querySelectorAll('img') ?? [])
        .slice(0, FRAME_IMAGE_SETTLE_MAX)
        .map(createImagePaintLease)
      doc = null
      const cancelImages = () => {
        for (const lease of leases) {
          lease.cancel()
        }
        leases.length = 0
      }
      cancelImageSettleRef.current?.()
      cancelImageSettleRef.current = cancelImages
      try {
        await settleWithin(
          Promise.allSettled(leases.map((lease) => lease.promise)),
          FRAME_IMAGE_SETTLE_TIMEOUT_MS
        )
      } finally {
        cancelImages()
        if (cancelImageSettleRef.current === cancelImages) {
          cancelImageSettleRef.current = null
        }
      }
      await afterAnimationFrame()
      await afterAnimationFrame()
      if (
        readyGenerationRef.current !== generation ||
        frameRef.current?.contentDocument !== loadedDocumentRef.current
      ) {
        return
      }
      measure()
      onReady?.()
    },
    [measure, onReady]
  )

  const bindConsumer = useCallback(
    (doc: Document, frame: HTMLIFrameElement) => {
      cleanupConsumerRef.current?.()
      cleanupConsumerRef.current = null
      const cleanup = onDocumentReadyRef.current?.(doc, frame)
      cleanupConsumerRef.current = cleanup ?? null
    },
    []
  )

  const handleLoad = useCallback(() => {
    const frame = frameRef.current
    const doc = frame?.contentDocument
    if (!frame || !doc?.documentElement) {
      return
    }
    if (loadedDocumentRef.current === doc) {
      bindConsumer(doc, frame)
      return
    }

    cleanupLayoutRef.current?.()
    cleanupConsumerRef.current?.()
    cancelImageSettleRef.current?.()
    cancelImageSettleRef.current = null
    cleanupLayoutRef.current = null
    cleanupConsumerRef.current = null
    loadedDocumentRef.current = doc

    const generation = readyGenerationRef.current + 1
    readyGenerationRef.current = generation
    measure()

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    resizeObserver?.observe(doc.documentElement)
    if (doc.body) {
      resizeObserver?.observe(doc.body)
    }

    const handleResourceSettle = () => measure()
    doc.addEventListener('load', handleResourceSettle, true)
    doc.addEventListener('error', handleResourceSettle, true)
    cleanupLayoutRef.current = () => {
      resizeObserver?.disconnect()
      doc.removeEventListener('load', handleResourceSettle, true)
      doc.removeEventListener('error', handleResourceSettle, true)
    }
    bindConsumer(doc, frame)

    void settle(generation)
  }, [bindConsumer, measure, settle])

  useEffect(() => {
    onDocumentReadyRef.current = onDocumentReady
    const doc = loadedDocumentRef.current
    const frame = frameRef.current
    if (doc && frame) {
      bindConsumer(doc, frame)
    }
  }, [bindConsumer, onDocumentReady])

  useEffect(() => {
    // `srcDoc` is present in the server markup, so a small document can finish
    // loading before hydration attaches React's onLoad listener. Re-enter the
    // same idempotent setup path once after hydration when that happened.
    const timer = setTimeout(() => {
      const doc = frameRef.current?.contentDocument
      if (doc?.body && doc.readyState !== 'loading') {
        handleLoad()
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [handleLoad])

  useEffect(
    () => () => {
      readyGenerationRef.current += 1
      cancelImageSettleRef.current?.()
      cancelImageSettleRef.current = null
      cleanupConsumerRef.current?.()
      cleanupLayoutRef.current?.()
      loadedDocumentRef.current = null
    },
    []
  )

  return (
    <iframe
      ref={frameRef}
      data-pixel-perfect-email-frame=""
      title={title}
      srcDoc={hardenedHtml}
      sandbox="allow-same-origin"
      referrerPolicy="no-referrer"
      scrolling="no"
      tabIndex={-1}
      className={className}
      onLoad={handleLoad}
      style={{
        width: '100%',
        maxWidth: '100%',
        height,
        display: 'block',
        overflow: 'hidden',
        border: 0,
        background: '#ffffff',
        ...style,
      }}
    />
  )
}
